import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { scrubIds } from './datadogScrub';
import { trackEvent, trackTiming } from './datadogTracking';

declare module 'axios' {
  // Per-request timing scratch space, stamped by the request interceptor and
  // read back in the response interceptor to compute `duration_ms`.
  export interface AxiosRequestConfig {
    metadata?: { startTime?: number };
  }
}

type HttpErrorType = 'http' | 'network' | 'other';

// Object-literal `type`s (not `interface`s): they carry the implicit index
// signature that the `EventAttrs` Record in `datadogTracking` requires.
type HttpTimingMetric = {
  durationMs: number;
  attrs: {
    method: string | undefined;
    status: number;
    url: string | undefined;
    success: true;
  };
};

type HttpErrorAttrs = {
  method: string | undefined;
  status: number | 'unknown';
  url: string | undefined;
  error_type: HttpErrorType;
};

/**
 * HTTP status from an axios-style error, or `'unknown'` when the request never
 * got a response (network error) or the error isn't HTTP-shaped. Shared by the
 * axios interceptor and the react-query cache `onError` handlers.
 */
export function httpStatus(error: unknown): number | 'unknown' {
  const status = (error as AxiosError)?.response?.status;
  return typeof status === 'number' ? status : 'unknown';
}

/**
 * Classify an axios error for the `error_type` facet:
 * - `http`    — server responded with an error status
 * - `network` — request was made but no response (timeout, offline, CORS)
 * - `other`   — setup/config error before the request was sent
 */
export function classifyAxiosError(error: unknown): HttpErrorType {
  const err = error as AxiosError;
  if (err?.response) {
    return 'http';
  }
  if (err?.request) {
    return 'network';
  }
  return 'other';
}

/**
 * URL for a RUM metric attribute: path only (query string dropped) with entity
 * ids scrubbed. Dropping the query avoids leaking free-text param values (e.g.
 * `?website=acme.com`, fund names) that `scrubIds` — which only redacts
 * numeric/UUID values — would otherwise let through, and keeps url cardinality
 * bounded for grouping.
 */
function metricUrl(url: string | undefined): string | undefined {
  if (url === undefined) {
    return undefined;
  }
  return scrubIds(url.split('?')[0]);
}

/**
 * A low-cardinality operation signature (`"METHOD /scrubbed/path"`) derived from
 * a rejected request's AxiosError config, or `'unknown'` when the error isn't
 * HTTP-shaped. Used as the mutation key_root fallback, since most `useMutation`
 * call sites don't set a `mutationKey`.
 */
export function requestSignature(error: unknown): string {
  const config = (error as AxiosError)?.config;
  if (!config?.url) {
    return 'unknown';
  }
  const method = config.method?.toUpperCase() ?? 'GET';
  return `${method} ${metricUrl(config.url) ?? config.url}`;
}

/**
 * Elapsed ms since the request interceptor stamped `startTime` on the config, or
 * `undefined` when no start time is present (request didn't run through
 * MaggieHttp's interceptor). Shared by the success and error timing paths.
 */
export function elapsedMs(config: InternalAxiosRequestConfig | undefined): number | undefined {
  const start = config?.metadata?.startTime;
  return typeof start === 'number' ? performance.now() - start : undefined;
}

/**
 * Build the `http.request` timing metric for a successful response, or `null`
 * when the request interceptor didn't stamp a start time. URLs are scrubbed of
 * entity IDs so RUM attributes stay groupable and leak-free.
 */
export function buildHttpTiming(response: AxiosResponse): HttpTimingMetric | null {
  const durationMs = elapsedMs(response.config as InternalAxiosRequestConfig | undefined);
  if (durationMs === undefined) {
    return null;
  }
  return {
    durationMs,
    attrs: {
      method: response.config?.method?.toUpperCase(),
      status: response.status,
      url: metricUrl(response.config?.url),
      success: true,
    },
  };
}

/** Build the `http.request_failed` attributes for a rejected request. */
export function buildHttpErrorAttrs(error: AxiosError): HttpErrorAttrs {
  return {
    method: error.config?.method?.toUpperCase(),
    status: httpStatus(error),
    url: metricUrl(error.config?.url),
    error_type: classifyAxiosError(error),
  };
}

/**
 * Emit `http.request` timing for a successful response. No-ops without a start
 * stamp. Wrapped in try/catch: metrics are best-effort and must never turn a
 * successful response into a rejection.
 */
export function emitHttpTiming(response: AxiosResponse): void {
  try {
    const metric = buildHttpTiming(response);
    if (metric) {
      trackTiming('http.request', metric.durationMs, metric.attrs);
    }
  } catch {
    // swallow — instrumentation must not affect the request flow
  }
}

/**
 * Emit `http.request_failed` for a rejected request, carrying `duration_ms` when
 * a start time is available (true for 4xx/5xx and timeouts alike), so failed and
 * successful request latency are comparable. Wrapped in try/catch so a throw
 * here can never pre-empt the interceptor's Sentry capture / on401 / the
 * rejected promise — the "metrics never alter propagation" guarantee, enforced.
 */
export function emitHttpError(error: AxiosError): void {
  try {
    const attrs = buildHttpErrorAttrs(error);
    const durationMs = elapsedMs(error.config);
    if (durationMs === undefined) {
      trackEvent('http.request_failed', attrs);
    } else {
      trackTiming('http.request_failed', durationMs, attrs);
    }
  } catch {
    // swallow — instrumentation must not affect error propagation
  }
}
